import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import './MobileCreateAvatar.css';

const IconUpload = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>;
const IconCheck2 = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconCamera = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>;

export default function MobileCreateAvatar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const returnTo  = location.state?.returnTo || '/m/avatars';
  const from      = location.state?.from;

  const [name, setName]             = useState('');
  const [file, setFile]             = useState(null);
  const [progress, setProgress]     = useState(0);
  const [uploading, setUploading]   = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState(false);
  const [createdName, setCreatedName] = useState('');

  const fileInputRef   = useRef(null);
  const cameraInputRef = useRef(null);
  const xhrRef         = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) {
      setError('请选择视频文件（MP4、MOV 等）');
      return;
    }
    if (f.size > 500 * 1024 * 1024) {
      setError('视频文件不能超过 500MB');
      return;
    }
    if (f.size < 1 * 1024 * 1024) {
      setError('视频文件太小，请选择大于 1MB 的视频');
      return;
    }
    setFile(f);
    setError('');
    setProgress(0);
  };

  const handleSubmit = async () => {
    if (!file) { setError('请先选择视频文件'); return; }
    if (!name.trim()) { setError('请输入形象名称'); return; }

    setUploading(true);
    setError('');
    setProgress(0);

    try {
      // Step 1: upload video
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      let uploadedUrl = '';

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const resp = JSON.parse(xhr.responseText);
              uploadedUrl = resp.data?.fileKey || resp.fileKey || resp.url || '';
              resolve();
            } catch {
              reject(new Error('上传响应解析失败'));
            }
          } else {
            reject(new Error(`上传失败 (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error('上传网络错误'));
        xhr.open('POST', '/api/upload-video');
        xhr.withCredentials = true;
        xhr.send(uploadFormData);
      });

      setProgress(100);
      setUploading(false);
      setProcessing(true);

      // Step 2: create model via /api/create-model
      const createResp = await fetch('/api/create-model', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), description: '', fileKey: uploadedUrl }),
      });
      const result = await createResp.json().catch(() => ({}));

      if (result && (result.code === 0 || result.success)) {
        setCreatedName(name.trim());
        setSuccess(true);
      } else {
        setError(result?.message || result?.msg || '创建失败，请重试');
      }
    } catch (err) {
      setError(err.message || '网络错误，请重试');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const handleDone = () => {
    navigate(from || returnTo, { replace: true });
  };

  return (
    <MobileLayout title="创建数字人" showBack>
      <div className="mca-root">
        {success ? (
          <div className="mca-success">
            <div className="mca-success-icon">🎭</div>
            <div className="mca-success-title">形象创建中！</div>
            <div className="mca-success-sub">
              「{createdName}」已提交训练，通常需要几分钟到几十分钟。
              训练完成后可在「我的形象」中看到。
            </div>
            <button className="mca-success-btn" onClick={handleDone}>
              返回选择形象
            </button>
          </div>
        ) : (
          <>
            {/* Video upload */}
            <div className="mca-card">
              <div className="mca-card-title">上传视频素材</div>

              {/* Hidden inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="video/*"
                capture="environment"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {file ? (
                /* Selected state */
                <div className="mca-selected-wrap">
                  <div className="mca-upload-icon success"><IconCheck2 /></div>
                  <div className="mca-upload-label">已选择视频</div>
                  <div className="mca-upload-filename">{file.name}</div>
                  <button
                    className="mca-reselect-btn"
                    onClick={() => { setFile(null); setProgress(0); }}
                  >
                    重新选择
                  </button>
                </div>
              ) : (
                /* Source selection */
                <div className="mca-source-row">
                  <button
                    className="mca-source-btn mca-source-btn--camera"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <div className="mca-source-icon mca-source-icon--camera">
                      <IconCamera />
                    </div>
                    <div className="mca-source-label">拍摄视频</div>
                    <div className="mca-source-hint">直接唤起摄像头</div>
                  </button>
                  <button
                    className="mca-source-btn mca-source-btn--gallery"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="mca-source-icon mca-source-icon--gallery">
                      <IconUpload />
                    </div>
                    <div className="mca-source-label">从相册选择</div>
                    <div className="mca-source-hint">选择已录制视频</div>
                  </button>
                </div>
              )}

              {/* Progress */}
              {(uploading || progress > 0) && (
                <div className="mca-progress-wrap">
                  <div className="mca-progress-bar">
                    <div className="mca-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mca-progress-label">
                    {progress < 100 ? `上传中 ${progress}%` : '上传完成，等待处理...'}
                  </div>
                </div>
              )}
              {processing && (
                <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', marginTop: 8 }}>
                  模型创建中，请稍候...
                </div>
              )}
            </div>

            {/* Name */}
            <div className="mca-card">
              <div className="mca-card-title">形象名称</div>
              <input
                className="mca-input"
                type="text"
                placeholder="给这个形象起个名字"
                maxLength={30}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Requirements */}
            <div className="mca-reqs">
              <div className="mca-reqs-title">📋 视频素材要求</div>
              <ul>
                <li>清晰正面出镜，光线充足</li>
                <li>时长建议 5~30 秒</li>
                <li>背景简洁，无遮挡面部</li>
                <li>格式：MP4、MOV，最大 500MB</li>
              </ul>
            </div>

            {/* Error */}
            {error && <div className="mca-error">{error}</div>}

            {/* Submit */}
            <button
              className="mca-submit"
              onClick={handleSubmit}
              disabled={uploading || processing}
            >
              {uploading || processing ? <span className="mca-spin" /> : '开始创建'}
            </button>
          </>
        )}
      </div>
    </MobileLayout>
  );
}
